import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, ArrowDown, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { api, BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const imgUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export default function PublicLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/public/landings/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const contact = async (source) => {
    setSending(true);
    try {
      const { data: r } = await api.post(`/public/landings/${slug}/interest`, { name, source });
      if (r.whatsapp_invite) window.open(r.whatsapp_invite, "_blank", "noopener");
      else toast.error("Este anunciante aún no configuró su WhatsApp.");
    } catch { toast.error("No se pudo abrir WhatsApp. Inténtalo de nuevo."); }
    finally { setSending(false); }
  };

  const scrollDetail = () => document.getElementById("detalle")?.scrollIntoView({ behavior: "smooth" });

  if (notFound) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-400">Esta página no existe o fue removida.</div>;
  }
  if (!data) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-purple-500" size={32} /></div>;
  }

  const [heroImg, bodyImg] = data.images || [];
  const benefits = (data.benefits || []).filter(Boolean);
  const story = (data.story || "").split("|").map((s) => s.trim()).filter(Boolean);

  return (
    <div data-testid="public-landing" className="min-h-screen bg-[#050505] text-zinc-100">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {heroImg && <img src={imgUrl(heroImg)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/85 to-[#050505]" />
        <div className="relative max-w-3xl mx-auto px-5 py-20 sm:py-28 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-purple-300 font-bold mb-5"><Sparkles size={13} /> {data.title}</span>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-5xl leading-tight">{data.headline}</h1>
          {data.subheadline && <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl mx-auto">{data.subheadline}</p>}
          <button data-testid="cta-saber-mas" onClick={scrollDetail} className="mt-10 mx-auto inline-flex flex-col items-center gap-1 text-zinc-400 hover:text-white text-sm font-medium transition-colors">
            Conoce toda la información <ArrowDown size={18} className="animate-bounce" />
          </button>
        </div>
      </section>

      {/* DETALLE */}
      <section id="detalle" className="max-w-3xl mx-auto px-5 py-16">
        {data.intro && <p className="text-lg sm:text-xl text-zinc-200 leading-relaxed text-center">{data.intro}</p>}

        {benefits.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-4 mt-12">
            {benefits.map((b) => (
              <div key={b} className="rounded-2xl border border-white/10 bg-[#121212] p-5">
                <CheckCircle2 className="text-purple-400 mb-2" size={22} />
                <p className="text-sm text-zinc-300">{b}</p>
              </div>
            ))}
          </div>
        )}

        {bodyImg && <img src={imgUrl(bodyImg)} alt="" className="w-full rounded-2xl border border-white/10 mt-12" />}

        {story.length > 0 && (
          <div className="mt-12 space-y-4">
            {story.map((p, i) => <p key={`${i}-${p.slice(0, 24)}`} className="text-zinc-300 leading-relaxed">{p}</p>)}
          </div>
        )}

        {/* CIERRE — aquí van los botones de contacto, al final de toda la información */}
        <div className="mt-16 rounded-3xl border border-green-500/30 bg-green-500/5 p-8 text-center">
          {data.closing && <p className="text-xl font-display font-bold text-zinc-50 mb-6">{data.closing}</p>}
          <div className="max-w-sm mx-auto">
            <Button data-testid="cta-platicar" onClick={() => contact("platicar")} disabled={sending} className="w-full bg-green-500 hover:bg-green-400 text-black font-bold rounded-full py-6 text-base mb-4">
              <MessageCircle size={18} className="mr-2" /> Quiero platicar contigo vía WhatsApp
            </Button>
            <div className="flex items-center gap-3 my-4 text-xs text-zinc-500">
              <span className="flex-1 h-px bg-white/10" /> o déjame tu nombre y te escribo <span className="flex-1 h-px bg-white/10" />
            </div>
            <Input data-testid="landing-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Escribe tu nombre (opcional)"
              className="bg-black border-white/15 rounded-xl py-6 text-center mb-3 focus-visible:ring-green-500" />
            <Button data-testid="cta-me-interesa" onClick={() => contact("interes")} disabled={sending} className="w-full bg-white hover:bg-zinc-200 text-black font-bold rounded-full py-6 text-base">
              {sending ? <Loader2 className="animate-spin mr-2" size={18} /> : <MessageCircle size={18} className="mr-2" />} ¡Me interesa! Contactar por WhatsApp
            </Button>
          </div>
        </div>

        {data.privacy_note && <p className="text-xs text-zinc-600 text-center mt-8">{data.privacy_note}</p>}
        <p className="text-center text-zinc-700 text-xs mt-6">Página creada con MULTICONTACTOS · Distribuidor independiente</p>
      </section>
    </div>
  );
}
